"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight, Cloud, Dices, Gamepad2, Heart, Sparkle, Star, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function () {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.15
  });
  const stickers = [{
    Icon: Sparkle,
    top: "8%",
    left: "6%",
    rotate: "-18deg",
    fill: "bg-emerald-500",
    size: "size-14 md:size-20",
    delay: "0s",
    hideOnMobile: false
  }, {
    Icon: Star,
    top: "14%",
    right: "8%",
    rotate: "12deg",
    fill: "bg-stone-50",
    size: "size-12 md:size-16",
    delay: "0.4s",
    hideOnMobile: false
  }, {
    Icon: Gamepad2,
    top: "62%",
    left: "4%",
    rotate: "8deg",
    fill: "bg-stone-50",
    size: "size-14 md:size-20",
    delay: "0.8s",
    hideOnMobile: true
  }, {
    Icon: Dices,
    top: "70%",
    right: "6%",
    rotate: "-10deg",
    fill: "bg-emerald-500",
    size: "size-12 md:size-16",
    delay: "1.2s",
    hideOnMobile: false
  }, {
    Icon: Heart,
    top: "30%",
    left: "12%",
    rotate: "20deg",
    fill: "bg-emerald-500",
    size: "size-10 md:size-14",
    delay: "0.2s",
    hideOnMobile: true
  }, {
    Icon: Zap,
    top: "44%",
    right: "10%",
    rotate: "-15deg",
    fill: "bg-stone-50",
    size: "size-12 md:size-16",
    delay: "0.6s",
    hideOnMobile: false
  }, {
    Icon: Cloud,
    top: "82%",
    left: "16%",
    rotate: "6deg",
    fill: "bg-stone-50",
    size: "size-10 md:size-14",
    delay: "1s",
    hideOnMobile: true
  }, {
    Icon: Sparkle,
    top: "20%",
    right: "18%",
    rotate: "-22deg",
    fill: "bg-emerald-500",
    size: "size-10 md:size-14",
    delay: "1.4s",
    hideOnMobile: true
  }];
  const tickerItems = ["ORGANIC HARVEST", "SEED SWAP", "COMPOSTING", "GREENHOUSE", "COMMUNITY PLOTS"];
  return <section data-section-id="1125" id="seasonal-planting-guide" className="relative overflow-hidden bg-emerald-900 py-24 md:py-44 dark bg-background text-foreground" style={{
    color: "hsl(92 25% 12%)"
  }}>
      {}
      <PixelCorner className="top-4 left-4" rotate="0" />
      <PixelCorner className="top-4 right-4" rotate="90deg" />
      <PixelCorner className="bottom-16 right-4" rotate="180deg" />
      <PixelCorner className="bottom-16 left-4" rotate="270deg" />

      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        {stickers.map((s, i) => <div data-index={i} key={i} className={["absolute flex items-center justify-center rounded-lg border-2 shadow-lg", s.fill, s.size, s.hideOnMobile ? "hidden md:flex" : "flex"].join(" ")} style={{
        top: s.top,
        left: s.left,
        right: s.right,
        borderColor: "hsl(92 25% 12%)",
        transform: `rotate(${s.rotate})`,
        animation: `wobble 4s ease-in-out ${s.delay} infinite alternate`
      }}>
            <s.Icon className="size-1/2" style={{
          color: "hsl(92 25% 12%)"
        }} strokeWidth={2.5} />
          </div>)}
      </div>

      {}
      <div ref={ref} className={["relative mx-auto max-w-[980px] px-6 text-center transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"].join(" ")}>
        {}
        <div className="mx-auto mb-10 inline-flex items-center font-mono text-xs md:text-sm font-medium uppercase" style={{
        color: "hsl(88 50% 55%)",
        letterSpacing: "0.18em"
      }}>
          § ECO GROW / SEASONAL
        </div>

        {}
        <h2 className="font-serif font-default tracking-tight" style={{
        fontSize: "clamp(3rem, 9vw, 8rem)",
        lineHeight: 0.88,
        letterSpacing: "-0.025em",
        color: "hsl(40 33% 96%)"
      }}>
          <span className="block">NEED A</span>
          <span className="relative inline-block my-2">
            <span className="relative inline-block px-6 md:px-10 py-1 md:py-2 border-2" style={{
            backgroundColor: "hsl(88 50% 55%)",
            borderColor: "hsl(40 33% 96%)",
            transform: "rotate(5deg)",
            borderRadius: "0.75rem"
          }}>
              GREEN
            </span>
            {}
            <svg aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 -bottom-4 md:-bottom-6 w-[110%]" viewBox="0 0 200 14" fill="none" preserveAspectRatio="none" style={{
            transform: "translateX(-50%) rotate(-2deg)",
            height: "10px"
          }}>
              <path d="M2 8 Q 20 1, 40 7 T 80 7 T 120 7 T 160 7 T 198 6" stroke="hsl(40 33% 96%)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
            </svg>
          </span>
          <span className="block">GUIDE?</span>
        </h2>

        {}
        <p className="mx-auto mt-12 md:mt-16 max-w-2xl font-default text-lg md:text-xl font-medium" style={{
        color: "hsl(40 33% 96%)",
        lineHeight: 1.5
      }}>
          Download our beginner-friendly planting guide to start your journey with Eco Grow. Cultivate your own harvest today.
        </p>

        {}
        <div className="mt-10 md:mt-14 flex flex-col items-center gap-6">
          <div className="relative w-full sm:w-auto px-6 sm:px-0">
            {}
            <svg aria-hidden="true" className="hidden md:block absolute -left-32 top-1/2 -translate-y-1/2 w-28 h-16" viewBox="0 0 120 60" fill="none">
              <path d="M4 12 Q 30 4, 50 22 T 95 36" stroke="hsl(40 33% 96%)" strokeWidth="2.5" strokeLinecap="round" fill="none" style={{
              strokeDasharray: 200,
              strokeDashoffset: isVisible ? 0 : 200,
              transition: "stroke-dashoffset 800ms ease-out 400ms"
            }} />
              <path d="M85 28 L 100 38 L 88 46" stroke="hsl(40 33% 96%)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" style={{
              strokeDasharray: 60,
              strokeDashoffset: isVisible ? 0 : 60,
              transition: "stroke-dashoffset 500ms ease-out 1100ms"
            }} />
            </svg>

            <Button asChild className="w-full sm:w-auto h-auto font-serif font-default text-xl md:text-2xl px-8 md:px-10 py-4 md:py-5 border-2 hover:translate-x-1 hover:translate-y-1 hover:shadow-lg transition-all duration-150 rounded-lg" style={{
            backgroundColor: "hsl(88 50% 55%)",
            color: "hsl(92 25% 12%)",
            borderColor: "hsl(40 33% 96%)"
          }}>
              <Link to="/JoinUsPage">
                DOWNLOAD GUIDE
                <ArrowRight className="ml-1 size-6" strokeWidth={3} />
              </Link>
            </Button>
          </div>

          <Link to="mailto:hello@ecogrow.org" className="font-default text-base md:text-lg font-medium border-b-2 border-dashed pb-1 hover:opacity-80 transition-opacity" style={{
          color: "hsl(40 33% 96%)",
          borderColor: "hsl(40 33% 96%)"
        }}>
            OR JOIN OUR WORKSHOPS — hello@ecogrow.org
          </Link>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 right-0 border-t-2 overflow-hidden py-3" style={{
      borderColor: "hsl(40 33% 96%)",
      backgroundColor: "hsl(92 25% 12%)"
    }}>
        <div className="flex whitespace-nowrap" style={{
        animation: "tickerScroll 60s linear infinite"
      }}>
          {[0, 1, 2, 3].map(dup => <div data-index={dup} key={dup} className="flex shrink-0 items-center">
              {tickerItems.map((item, i) => <span data-index={i} key={`${dup}-${i}`} className="font-mono text-xs md:text-sm font-medium uppercase px-6 flex items-center gap-6" style={{
            color: "hsl(40 33% 96%)",
            letterSpacing: "0.12em"
          }}>
                  <span aria-hidden="true">◆</span>
                  {item}
                </span>)}
            </div>)}
        </div>
      </div>

      <style>{`
        @keyframes wobble {
          from { transform: rotate(var(--r, 0deg)) translateY(0px); }
          to { transform: rotate(var(--r, 0deg)) translateY(-6px); }
        }
        @keyframes tickerScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-25%); }
        }
      `}</style>
    </section>;
}
function PixelCorner({
  className,
  rotate
}: {
  className?: string;
  rotate: string;
}) {
  return <div aria-hidden="true" className={["absolute pointer-events-none", className].join(" ")} style={{
    transform: `rotate(${rotate})`
  }}>
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="0" y="0" width="6" height="2" fill="hsl(40 33% 96%)" />
        <rect x="0" y="0" width="2" height="6" fill="hsl(40 33% 96%)" />
      </svg>
    </div>;
}